/**
 * Claimed Rewards tab — searchable list of reward redemptions.
 *
 * Shows who claimed what reward, when, points spent, and status.
 * Search is debounced 300ms. Pagination via "Load more" button.
 *
 * Plan 179, Epic 04 — Fulfillment Tracking
 */

import { useState, useEffect, useRef } from "react";
import { Search, Gift } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useClaimedRewards,
  type ClaimedRewardItem,
} from "../api/rewardsAdminHooks";

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  Claimed: {
    label: "รอดำเนินการ",
    className: "bg-amber-100 text-amber-700 border-amber-200",
  },
  Fulfilled: {
    label: "สำเร็จ",
    className: "bg-green-100 text-green-700 border-green-200",
  },
  Cancelled: {
    label: "ยกเลิก",
    className: "bg-red-100 text-red-700 border-red-200",
  },
};

const TYPE_LABELS: Record<string, string> = {
  coupon: "คูปอง",
  free_item: "ของรางวัล",
  custom: "รางวัล",
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return "-";
  }
}

export function ClaimedRewardsTab() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [previousPages, setPreviousPages] = useState<ClaimedRewardItem[]>([]);
  const [offset, setOffset] = useState(0);

  // Debounce search input — only fires when value actually changes
  const prevSearchRef = useRef(search);
  useEffect(() => {
    if (search === prevSearchRef.current) return;
    prevSearchRef.current = search;
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
      setPreviousPages([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const claimsQuery = useClaimedRewards(
    undefined,
    debouncedSearch,
    PAGE_SIZE,
    offset,
  );

  // Derive display list directly from query data — no effect delay
  const currentPageClaims = claimsQuery.data?.claims ?? [];
  const allClaims = offset === 0 ? currentPageClaims : [...previousPages, ...currentPageClaims];
  const hasMore = claimsQuery.data?.has_more ?? false;

  const handleLoadMore = () => {
    setPreviousPages(allClaims);
    setOffset((prev) => prev + PAGE_SIZE);
  };

  if (claimsQuery.isLoading && allClaims.length === 0) {
    return (
      <div className="space-y-3 pt-4">
        <Skeleton className="h-10 rounded-lg" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ค้นหาชื่อลูกค้าหรือรางวัล..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Claims list */}
      {allClaims.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Gift className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            {debouncedSearch
              ? "ไม่พบการแลกรางวัลที่ค้นหา"
              : "ยังไม่มีการแลกรางวัล"}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {allClaims.map((claim) => {
            const statusStyle = STATUS_STYLES[claim.status] ?? STATUS_STYLES.Claimed;
            const typeLabel = TYPE_LABELS[claim.reward_type] ?? claim.reward_type;

            return (
              <div
                key={claim.name}
                className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {claim.customer_name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {claim.reward_title}
                    <span className="mx-1">·</span>
                    {typeLabel}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-amber-600 whitespace-nowrap">
                      {claim.points_spent.toLocaleString()} แต้ม
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(claim.claimed_at)}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`shrink-0 text-xs ${statusStyle.className}`}
                  >
                    {statusStyle.label}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLoadMore}
          disabled={claimsQuery.isFetching}
          className="w-full"
        >
          {claimsQuery.isFetching ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
        </Button>
      )}
    </div>
  );
}
